/**
 * Component decorator system for ECS-like architecture.
 * Метаданные хранятся в WeakMap и Symbol на классе.
 */

import type { FieldType, FieldDef } from "../ComponentRegistry";

// ============================================================================
// TYPES
// ============================================================================

export interface FieldOptions {
  type: FieldType;
  label?: string;
  tooltip?: string;
  default?: unknown;
  min?: number;
  max?: number;
  step?: number;
  group?: string;
  hidden?: boolean;
  readonly?: boolean;
  /** For asset fields */
  assetType?: string;
  /** For array/object fields */
  elementType?: FieldType;
  /**
   * Backing storage for this field.
   * - `'aos'` (default): plain object on the GameObject (Array-of-Structs).
   * - `'soa'`: packed into a typed-array column (Structure-of-Arrays) — the hot/GPU/
   *   C#-friendly path. Only meaningful for fixed-size numeric/bool/vec/color fields.
   *   Materialized into column layout + struct-of-getter Views by the component codegen.
   */
  storage?: "aos" | "soa";
  /**
   * Inspector widget override. If omitted, the auto-renderer infers one from `type` + hints
   * (numeric with min&max → slider; `options` present → dropdown; `asset` → asset picker;
   * `entity` → object-ref; `vecN` → vector). Set explicitly to force a widget.
   */
  widget?: FieldWidget;
  /** Choices for `dropdown`/`enum` widgets (e.g. enum-as-number fields). */
  options?: FieldChoice[];
}

/** Inspector widget kinds the auto-renderer can dispatch to. */
export type FieldWidget =
  | "slider"
  | "dropdown"
  | "enum"
  | "color"
  | "asset"
  | "objectRef"
  | "vector"
  | "toggle"
  | "text"
  | "number";

/** A single choice for a dropdown/enum field. */
export interface FieldChoice {
  label: string;
  value: unknown;
}

export interface FieldMetadata extends FieldOptions {
  name: string;
  propertyKey: string;
  /** Resolved backing storage (defaults to `'aos'` when unspecified). */
  storage?: "aos" | "soa";
}

export interface ComponentMetadata {
  typeId: string;
  displayName?: string;
  icon?: string;
  color?: string;
  category?: string;
  requires?: string[];
  executeInEditor?: boolean;
  allowMultiple?: boolean;
  fields: Map<string, FieldMetadata>;
  /** Runtime-only property names (`@runtime`) — live scratch state that is NEVER serialized. */
  runtimeFields?: Set<string>;
  /** Bespoke editor name (`@customEditor`) — resolved to a registered Vue editor in the inspector. */
  customEditor?: string;
  /** Constructor reference for runtime instantiation */
  classRef?: new () => unknown;
}

// ============================================================================
// STORAGE
// ============================================================================

/** Symbol для хранения метаданных на классе */
export const COMPONENT_METADATA = Symbol.for("component:metadata");

/** WeakMap для хранения метаданных до применения class decorator */
const pendingFields = new WeakMap<object, Map<string, FieldMetadata>>();

/**
 * TC39 (stage 3) decorators pass `undefined` as the first arg to field decorators,
 * so we can't key by prototype. We accumulate metadata in a temp array and flush
 * it inside the class decorator (which always runs last for a given class).
 */
const tc39PendingFields: FieldMetadata[] = [];

/** Pending `@runtime` property names (legacy: per-prototype; TC39: temp array), flushed by @component. */
const pendingRuntimeFields = new WeakMap<object, Set<string>>();
const tc39PendingRuntimeFields: string[] = [];

/** Returns true when the second argument looks like a TC39 DecoratorContext */
function isTC39Context(arg: unknown): arg is { kind: string; name: string | symbol } {
  return typeof arg === 'object' && arg !== null && 'kind' in arg;
}

/** Глобальный реестр всех компонентов */
export const componentRegistry = new Map<string, ComponentMetadata>();

/** Получить или создать метаданные для класса */
function getOrCreateMetadata(target: object): ComponentMetadata {
  let metadata = (target as any)[COMPONENT_METADATA] as ComponentMetadata | undefined;
  if (!metadata) {
    metadata = {
      typeId: "",
      fields: new Map(),
    };
    (target as any)[COMPONENT_METADATA] = metadata;
  }
  return metadata;
}

/** Получить pending fields для prototype */
function getPendingFields(prototype: object): Map<string, FieldMetadata> {
  let fields = pendingFields.get(prototype);
  if (!fields) {
    fields = new Map();
    pendingFields.set(prototype, fields);
  }
  return fields;
}

// ============================================================================
// CLASS DECORATORS
// ============================================================================

/**
 * Main component decorator - marks a class as a component
 * @param typeId Unique component type identifier (e.g., "core.SpriteRenderer")
 */
export function component(typeId: string): ClassDecorator {
  return function (target) {
    const proto = (target as unknown as Function).prototype;
    const metadata = getOrCreateMetadata(proto);
    metadata.typeId = typeId;

    // Merge legacy pending fields / hooks (keyed by prototype)
    const fields = pendingFields.get(proto);
    if (fields) {
      fields.forEach((field, key) => metadata.fields.set(key, field));
    }

    // Merge TC39 pending fields (accumulated in a temp array)
    for (const f of tc39PendingFields) {
      metadata.fields.set(f.propertyKey, f);
    }
    tc39PendingFields.length = 0;

    // Merge @runtime property names (legacy per-prototype + TC39 temp array).
    const runtimeNames = pendingRuntimeFields.get(proto);
    if (runtimeNames || tc39PendingRuntimeFields.length) {
      const set = metadata.runtimeFields ?? new Set<string>();
      runtimeNames?.forEach((n) => set.add(n));
      for (const n of tc39PendingRuntimeFields) set.add(n);
      tc39PendingRuntimeFields.length = 0;
      metadata.runtimeFields = set;
    }

    // Store class constructor for runtime instantiation
    metadata.classRef = target as unknown as new () => unknown;

    // Register in global registry
    componentRegistry.set(typeId, metadata);

    // Store on class itself for runtime access
    (target as any)[COMPONENT_METADATA] = metadata;

    return target;
  };
}

/**
 * Set display name for editor UI
 */
export function displayName(name: string): ClassDecorator {
  return function (target) {
    getOrCreateMetadata(target.prototype).displayName = name;
    return target;
  };
}

/**
 * Bespoke inspector opt-in (Unity-style `[CustomEditor]`). Marks a component or Behaviour as
 * rendered by the registered Vue editor `name` (see the editor's script-editor registry) instead
 * of the auto field-renderer. Declarative `@field({ widget, options })` hints cover most cases;
 * reach for this only when a script needs a hand-authored inspector.
 */
export function customEditor(name: string): ClassDecorator {
  return function (target) {
    getOrCreateMetadata((target as unknown as { prototype: object }).prototype).customEditor = name;
    return target;
  };
}

/**
 * Set icon for editor UI (Lucide icon name)
 */
export function icon(iconName: string): ClassDecorator {
  return function (target) {
    getOrCreateMetadata(target.prototype).icon = iconName;
    return target;
  };
}

/**
 * Set color for editor UI (hex color string)
 */
export function color(hexColor: string): ClassDecorator {
  return function (target) {
    getOrCreateMetadata(target.prototype).color = hexColor;
    return target;
  };
}

/**
 * Set category for editor grouping
 */
export function category(categoryName: string): ClassDecorator {
  return function (target) {
    getOrCreateMetadata(target.prototype).category = categoryName;
    return target;
  };
}

/**
 * Specify required components
 */
export function requireComponent(components: string[]): ClassDecorator {
  return function (target) {
    getOrCreateMetadata(target.prototype).requires = components;
    return target;
  };
}

/**
 * Whether component's hooks run in editor mode
 */
export function executeInEditor(value: boolean): ClassDecorator {
  return function (target) {
    getOrCreateMetadata(target.prototype).executeInEditor = value;
    return target;
  };
}

/**
 * Whether multiple instances of this component can exist on same entity
 */
export function allowMultiple(value: boolean): ClassDecorator {
  return function (target) {
    getOrCreateMetadata(target.prototype).allowMultiple = value;
    return target;
  };
}

// ============================================================================
// FIELD DECORATOR
// ============================================================================

/**
 * Mark a property as a component field.
 * Supports both legacy (`experimentalDecorators`) and TC39 (stage-3) decorator APIs.
 */
export function field(options: FieldOptions) {
  return function (target: unknown, propertyKeyOrCtx: unknown) {
    if (isTC39Context(propertyKeyOrCtx)) {
      // TC39 decorators – target is `undefined`, context carries the name
      const key = String(propertyKeyOrCtx.name);
      tc39PendingFields.push({
        ...options,
        name: options.label ?? key,
        propertyKey: key,
      });
    } else {
      // Legacy decorators – target is the prototype
      const key = String(propertyKeyOrCtx);
      const fields = getPendingFields(target as object);
      fields.set(key, {
        ...options,
        name: options.label ?? key,
        propertyKey: key,
      });
    }
  } as PropertyDecorator;
}

/**
 * Mark a property as RUNTIME-ONLY: live scratch state that exists on the instance but is NEVER
 * serialized (the serializer excludes `@runtime` names) and is not an authored `@field`. Use for
 * per-frame state the engine writes (e.g. a CameraRig's current position/velocity) instead of
 * leaking it into saved levels. Does not affect the property's value/behaviour at runtime.
 */
export function runtime(target: unknown, propertyKeyOrCtx: unknown): void {
  if (isTC39Context(propertyKeyOrCtx)) {
    tc39PendingRuntimeFields.push(String(propertyKeyOrCtx.name));
  } else {
    const proto = target as object;
    let set = pendingRuntimeFields.get(proto);
    if (!set) {
      set = new Set();
      pendingRuntimeFields.set(proto, set);
    }
    set.add(String(propertyKeyOrCtx));
  }
}

// ============================================================================
// UTILITIES
// ============================================================================

/**
 * Get component metadata from a class or instance
 */
export function getComponentMetadata(target: unknown): ComponentMetadata | undefined {
  if (!target) return undefined;

  // If it's an instance, get the constructor
  const ctor = typeof target === "function" ? target : (target as object).constructor;

  return (ctor as any)[COMPONENT_METADATA] as ComponentMetadata | undefined;
}

/**
 * Read a class's `@field` metadata WITHOUT requiring a class decorator. Works for:
 *  - `@component` classes (fields flushed into COMPONENT_METADATA), AND
 *  - bare classes (e.g. a `Behaviour`) that only use `@field` — read from the pending map.
 *
 * Legacy-decorator path (`experimentalDecorators`, which this project uses): `@field` stores
 * per-prototype, so an undecorated Behaviour's fields are still recoverable. (TC39 fields can't
 * be attributed per-class without a class decorator and are intentionally not handled here.)
 */
export function getFieldMetadata(target: unknown): FieldMetadata[] {
  if (!target) return [];
  const ctor = typeof target === "function" ? target : (target as object).constructor;
  const meta = (ctor as any)[COMPONENT_METADATA] as ComponentMetadata | undefined;
  if (meta && meta.fields.size > 0) return [...meta.fields.values()];
  const pending = pendingFields.get((ctor as any).prototype);
  return pending ? [...pending.values()] : [];
}

/** Convert decorator `FieldMetadata` to the editor-facing registry `FieldDef`. */
export function fieldMetadataToFieldDef(meta: FieldMetadata): FieldDef {
  const def: FieldDef = {
    name: meta.propertyKey,
    type: meta.type,
    default: meta.default,
    elementType: meta.elementType,
    assetType: meta.assetType,
  };

  if (
    meta.hidden !== undefined ||
    meta.readonly !== undefined ||
    meta.label !== undefined ||
    meta.tooltip !== undefined ||
    meta.min !== undefined ||
    meta.max !== undefined ||
    meta.step !== undefined ||
    meta.group !== undefined ||
    meta.widget !== undefined ||
    meta.options !== undefined
  ) {
    def.editor = {
      hidden: meta.hidden,
      readonly: meta.readonly,
      label: meta.label,
      tooltip: meta.tooltip,
      min: meta.min,
      max: meta.max,
      step: meta.step,
      group: meta.group,
      widget: meta.widget,
      options: meta.options,
    };
  }

  return def;
}

/** Read a class's `@field` metadata as editor-facing `FieldDef[]` (components OR behaviours). */
export function getFieldDefs(target: unknown): FieldDef[] {
  return getFieldMetadata(target).map(fieldMetadataToFieldDef);
}

/**
 * Read a class's `@customEditor` name, if any (components OR behaviours). Checks the constructor's
 * metadata (`@component` classes) then the prototype's (behaviours that only use class decorators).
 */
export function getCustomEditorName(target: unknown): string | null {
  if (!target) return null;
  const ctor = typeof target === "function" ? target : (target as object).constructor;
  const onCtor = (ctor as any)[COMPONENT_METADATA] as ComponentMetadata | undefined;
  if (onCtor?.customEditor) return onCtor.customEditor;
  const onProto = (ctor as any).prototype?.[COMPONENT_METADATA] as ComponentMetadata | undefined;
  return onProto?.customEditor ?? null;
}

/**
 * Get all registered components
 */
export function getAllComponents(): Map<string, ComponentMetadata> {
  return componentRegistry;
}

/**
 * Get component by typeId
 */
export function getComponentByTypeId(typeId: string): ComponentMetadata | undefined {
  return componentRegistry.get(typeId);
}
